import { events } from '@/app/events/events';
import { fileHasData } from '@/app/gridGL/helpers/fileHasData';
import { readFileEmptyStateShown, writeFileEmptyStateShown } from '@/app/ui/hooks/fileEmptyStateSession';
import { useConnectionsFetcher } from '@/app/ui/hooks/useConnectionsFetcher';
import { hasPendingAgentSetup } from '@/shared/components/connections/pendingCustomAgentSetup';
import { useFileRouteLoaderData } from '@/shared/hooks/useFileRouteLoaderData';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

export const useFileHasData = (): boolean => {
  const [hasData, setHasData] = useState(fileHasData());

  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

    const checkData = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        const current = fileHasData();
        setHasData((prev) => (prev !== current ? current : prev));
      }, 100);
    };

    events.on('hashContentChanged', checkData);
    return () => {
      events.off('hashContentChanged', checkData);
      clearTimeout(debounceTimeout);
    };
  }, []);

  return hasData;
};

// Exported so non-React flows (e.g. resuming a chat after a Stripe upgrade in
// UrlParamsUser) can dismiss the full-screen empty-state overlay so it doesn't
// cover the AI Analyst.
export const fileEmptyStateDismissedAtom = atom(false);

// Module-level tracking guard so events fire exactly once per file,
// regardless of how many components call useShowFileEmptyState.
let trackedForFileUuid: string | null = null;

// Module-level tracking for dismissed state reset across component remounts
let lastDismissedForUuid: string | null = null;

// Whether the empty state was already shown for a file, captured the first time
// we see that file and BEFORE we persist the "shown" flag for it. The hook has
// several consumers (grid overlay, sidebar, toolbar, etc.); reading
// sessionStorage per-instance during render would flip to `true` the moment
// writeFileEmptyStateShown runs, so a consumer that (re)mounts after the write
// would suppress the empty state while others still show it (e.g. the sidebar
// greys out but the grid overlay never appears). Caching per uuid keeps every
// consumer consistent and stable across the write.
let alreadyShownSnapshot: { uuid: string; alreadyShown: boolean } | null = null;

const getAlreadyShownForUuid = (uuid: string): boolean => {
  // `alreadyShownSnapshot` starts null, and on routes without a `:uuid` param
  // (e.g. /embed, where SheetBar still renders) `uuid` is undefined. Checking
  // `alreadyShownSnapshot?.uuid !== uuid` was `undefined !== undefined` in that
  // case (false), so the snapshot was never initialized and the return
  // dereferenced null. Initialize whenever the snapshot is missing or stale.
  if (!alreadyShownSnapshot || alreadyShownSnapshot.uuid !== uuid) {
    alreadyShownSnapshot = { uuid, alreadyShown: readFileEmptyStateShown()?.fileUuid === uuid };
  }
  return alreadyShownSnapshot.alreadyShown;
};

// Sticky per-uuid snapshot of `hasPendingAgentSetup()`, captured during the
// first synchronous render of useShowFileEmptyState for a given file.
// `useConsumePendingAgentSetup` (mounted inside `<AIAnalyst />`, a child of
// QuadraticUI) clears the sessionStorage entry in a child effect — which
// runs BEFORE the parent's reset useEffect. Without this snapshot the
// parent effect would re-evaluate `hasPendingAgentSetup()` as false and
// re-introduce the empty-state overlay on top of the AI chat we're about
// to drive.
const pendingAgentSetupSnapshotByUuid = new Map<string, boolean>();
const snapshotPendingAgentSetup = (uuid: string): boolean => {
  const existing = pendingAgentSetupSnapshotByUuid.get(uuid);
  if (existing !== undefined) return existing;
  const value = hasPendingAgentSetup();
  pendingAgentSetupSnapshotByUuid.set(uuid, value);
  return value;
};

export const useShowFileEmptyState = () => {
  const dismissed = useAtomValue(fileEmptyStateDismissedAtom);
  const setDismissed = useSetAtom(fileEmptyStateDismissedAtom);
  const dismiss = useCallback(() => setDismissed(true), [setDismissed]);

  // Capture whether the file was empty at mount time (once per file)
  const { uuid } = useParams() as { uuid: string };
  const [searchParams] = useSearchParams();
  const hasStateParam = searchParams.has('state');
  const skipForPendingAgentSetup = snapshotPendingAgentSetup(uuid);
  const wasEmptyOnLoadRef = useRef(!fileHasData() && !hasStateParam && !skipForPendingAgentSetup);

  // Suppress the empty state if it was already shown for this file in this tab
  // session (e.g. the user saw it, then refreshed while the file is still
  // empty). Kept separate from wasEmptyOnLoadRef so the analytics below still
  // report "shown" vs "skipped" based purely on whether the file was empty.
  const alreadyShown = getAlreadyShownForUuid(uuid);

  useEffect(() => {
    if (uuid !== lastDismissedForUuid) {
      lastDismissedForUuid = uuid;
      // Skip empty state if there's a state param (import from dashboard with
      // code to insert) or the user navigated in to drive an agent-connection
      // setup (the AI chat needs the foreground).
      wasEmptyOnLoadRef.current = !fileHasData() && !hasStateParam && !skipForPendingAgentSetup;
      setDismissed(false);
    }
  }, [uuid, setDismissed, hasStateParam, skipForPendingAgentSetup]);

  // Auto-dismiss when file gets data (e.g. import, typing)
  useEffect(() => {
    if (dismissed) return;

    const checkData = () => {
      if (fileHasData()) {
        setDismissed(true);
      }
    };

    events.on('hashContentChanged', checkData);
    return () => {
      events.off('hashContentChanged', checkData);
    };
  }, [dismissed, setDismissed]);

  // Use the route loader data for the initial synchronous value (avoids flash),
  // then let the connections fetcher override once loaded (reflects runtime changes).
  const fileRouteData = useFileRouteLoaderData();
  const initialHasConnections = fileRouteData?.team?.hasConnections ?? false;
  const { connections, isLoading: connectionsLoading } = useConnectionsFetcher();
  const hasConnections = connectionsLoading ? initialHasConnections : connections.length > 0;

  const showEmptyState = wasEmptyOnLoadRef.current && !dismissed && !hasConnections && !alreadyShown;

  useEffect(() => {
    if (connectionsLoading || trackedForFileUuid === uuid) return;
    trackedForFileUuid = uuid;

    if (wasEmptyOnLoadRef.current) {
      if (hasConnections) {
        trackEvent('[EmptyState].skipped', { reason: 'has_connections' });
      } else if (alreadyShown) {
        trackEvent('[EmptyState].skipped', { reason: 'already_shown' });
      } else {
        trackEvent('[EmptyState].shown');
        // Persist so a refresh of this still-empty file won't show it again.
        writeFileEmptyStateShown(uuid);
      }
    } else if (skipForPendingAgentSetup) {
      trackEvent('[EmptyState].skipped', { reason: 'pending_agent_setup' });
    } else if (hasStateParam) {
      trackEvent('[EmptyState].skipped', { reason: 'has_state_param' });
    } else {
      trackEvent('[EmptyState].skipped', { reason: 'file_has_data' });
    }
  }, [connectionsLoading, hasConnections, uuid, hasStateParam, alreadyShown, skipForPendingAgentSetup]);

  return { showEmptyState, dismiss };
};
